import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  House,
  CalendarBlank,
  Users,
  CalendarCheck,
  Scissors,
  UserCircle,
  FileText,
  CreditCard,
  ChartLine,
  Gear,
  SignOut,
  Crown,
  Stack,
} from '@phosphor-icons/react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: House },
  { path: '/diary', label: 'Diary', icon: CalendarBlank },
  { path: '/clients', label: 'Clients', icon: Users },
  { path: '/appointments', label: 'Appointments', icon: CalendarCheck },
  { path: '/services', label: 'Services', icon: Scissors },
  { path: '/courses', label: 'Courses', icon: Stack },
  { path: '/staff', label: 'Staff', icon: UserCircle },
  { path: '/forms', label: 'Forms & Records', icon: FileText },
  { path: '/payments', label: 'Payments', icon: CreditCard },
  { path: '/reports', label: 'Reports', icon: ChartLine },
  { path: '/settings', label: 'Settings', icon: Gear },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside 
      className="fixed left-0 top-0 h-screen w-64 bg-white/80 backdrop-blur-md border-r border-border/50 flex flex-col z-50"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-6 border-b border-border/50">
        <img 
          src="https://customer-assets.emergentagent.com/job_skincare-platform-1/artifacts/48p1gbwz_The-Skin-Nurse-Northampton.png"
          alt="The Skin Nurse"
          className="h-10 object-contain"
          data-testid="sidebar-logo"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3" data-testid="sidebar-nav">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-nav-item ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon size={20} weight="regular" />
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User info & logout */}
      <div className="p-4 border-t border-border/50">
        {/* God Mode link for platform admins */}
        {user?.is_platform_admin && (
          <NavLink
            to="/platform"
            className="flex items-center gap-2 px-3 py-2 mb-3 text-sm font-medium rounded-lg bg-zinc-900 text-amber-400 hover:bg-zinc-800 transition-colors"
            data-testid="nav-god-mode"
          >
            <Crown size={18} weight="fill" />
            <span>God Mode</span>
          </NavLink>
        )}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary font-medium">
            {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" data-testid="user-name">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-muted-foreground capitalize" data-testid="user-role">
              {user?.role}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/5 transition-colors"
          data-testid="logout-btn"
        >
          <SignOut size={18} />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
};
